import React, { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { View } from 'common/types/view';
import { isBlob, isHref } from 'common/views/view_types';
import { useAsyncValue } from 'common/providers/AsyncValues/useAsyncValue';
import { Nullable } from 'common/types/nullable';
import { fetchRowCountFromAPI } from 'common/util/rowCount';
import { DebugTallyTracker } from './DebugTallyTracker';
import { RowCountValue } from './types';
import RowCountContext from './RowCountContext';
import RowCountUtils from './RowCountUtils';

/** RowCountProvider component properties. */
export interface RowCountProviderProps {
  view: View;
  children: ReactNode;
  enableCaching?: boolean;
  query?: string;
}

/** Optimized row count provider that lazy loads counts on demand, and doesn't refetch unless necessary. */
export const RowCountProvider: React.FC<RowCountProviderProps> = ({
  view,
  children,
  enableCaching,
  query,
}) => {
  // Use URL param if enableCaching not explicitly set
  const cachingEnabled = enableCaching ?? RowCountUtils.getCachingSettingFromURL();
  // Create debug tracker instance
  const debugTracker = useMemo(() => new DebugTallyTracker(RowCountUtils.getDebugSettingFromURL()), []);

  // Load total count - only when requested
  const loadTotalCount = useCallback(async (): Promise<Nullable<number>> => {
    return !isBlob(view) && !isHref(view) && view?.id && window?.location?.hostname
      ? await fetchRowCountFromAPI(view.id, view)
      : 0;
  }, [view]);

  // Load filtered count - only when requested
  const loadFilteredCount = useCallback(async (): Promise<Nullable<number>> => {
    // Use query directly from closure - this should work now with proper dependencies
    const currentQuery = query?.trim() || undefined;
    return !isBlob(view) && !isHref(view) && view?.id && window?.location?.hostname
      ? await fetchRowCountFromAPI(view.id, view, currentQuery)
      : 0;
  }, [view, query]);

  // Separate async values for each count type - only load when accessed
  const totalCountAsync = useAsyncValue(loadTotalCount);
  const filteredCountAsync = useAsyncValue(loadFilteredCount);

  // Reset and refetch data when dependencies change
  useEffect(() => {
    totalCountAsync.refetch().catch(() => {}); // Ignore errors from cancelled requests
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cachingEnabled]);

  useEffect(() => {
    filteredCountAsync.refetch().catch(() => {}); // Ignore errors from cancelled requests
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, query, cachingEnabled]);

  const contextValue: RowCountValue = useMemo(
    () => ({
      totalCount: totalCountAsync,
      filteredCount: filteredCountAsync,
      debugTracker,
    }),
    [totalCountAsync, filteredCountAsync, debugTracker],
  );

  return <RowCountContext.Provider value={contextValue}>{children}</RowCountContext.Provider>;
};
